import * as THREE from 'three'
import { colorDictionary } from '~/utils/colorDictionary'

const textureCache = new Map<string, THREE.Texture>()

export const preloadPBRTextures = (): void => {
  const textureLoader = new THREE.TextureLoader()

  colorDictionary.forEach((colorItem) => {
    if (!colorItem.pbrTextures) return
    const textures = colorItem.pbrTextures

    const textureKeys = [
      'diffuse',
      'normal',
      'roughness',
      'aoRoughMetal',
      'displacement',
      'envMap',
    ] as const
    textureKeys.forEach((key) => {
      const url = textures[key]
      if (!url || textureCache.has(url)) return

      const texture = textureLoader.load(
        url,
        (loadedTexture) => {
          if (key === 'diffuse') {
            loadedTexture.colorSpace = THREE.NoColorSpace
          }
          loadedTexture.wrapS = THREE.RepeatWrapping
          loadedTexture.wrapT = THREE.RepeatWrapping
          loadedTexture.needsUpdate = true
        },
        undefined,
        (error) => {
          console.warn(`Failed to load texture: ${url}`, error)
        }
      )

      if (key === 'diffuse') {
        texture.colorSpace = THREE.NoColorSpace
      }
      texture.wrapS = THREE.RepeatWrapping
      texture.wrapT = THREE.RepeatWrapping

      textureCache.set(url, texture)
    })
  })
}

export const getCachedTexture = (url: string): THREE.Texture | null => {
  return textureCache.get(url) || null
}

// Texture ownership protocol:
// - Textures returned by `getCachedTexture` are shared with the drei
//   `useGLTF` cache and every clone of every Object3D that referenced
//   the original GLTF. Disposing them frees GPU buffers that other
//   clones still expect → corrupts the whole scene.
// - Textures *we* create per-mesh (via `.clone()` in the PBR pipeline)
//   are local to a single material and must be disposed when that
//   material is replaced or its host mesh unmounts.
// We mark the latter with `userData.owned = true` and gate disposal on it.
export const markTextureOwned = (texture: THREE.Texture): THREE.Texture => {
  texture.userData.owned = true
  return texture
}

const disposeIfOwned = (texture: unknown): void => {
  if (texture instanceof THREE.Texture && texture.userData.owned === true) {
    texture.dispose()
  }
}

export const disposeMaterialOwnedTextures = (
  material: THREE.Material
): void => {
  if ('map' in material) disposeIfOwned((material as { map?: unknown }).map)
  if ('normalMap' in material)
    disposeIfOwned((material as { normalMap?: unknown }).normalMap)
  if ('roughnessMap' in material)
    disposeIfOwned((material as { roughnessMap?: unknown }).roughnessMap)
  if ('metalnessMap' in material)
    disposeIfOwned((material as { metalnessMap?: unknown }).metalnessMap)
  if ('aoMap' in material)
    disposeIfOwned((material as { aoMap?: unknown }).aoMap)
  if ('displacementMap' in material)
    disposeIfOwned((material as { displacementMap?: unknown }).displacementMap)
}
